import clsx from "clsx";

import { Button } from "V2.0/common/button";
import InputComponent from "V2.0/components/InputComponent";
import { Field, Label, Description } from "V2.0/common/fieldset";
import { RadioGroup, RadioField, Radio } from "V2.0/common/radio";
import { Badge } from "V2.0/common/badge";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

// Three-way mode backing this card's radio selector. "Off" has no
// equivalent in the old switch-based UI (both EOP switches simply stayed
// unchecked) -- it's promoted to an explicit, selectable option here since
// the redesigned card always shows exactly one selected mode.
export enum EndOfPhraseMode {
  OFF = "off",
  SEMANTIC = "semantic",
  AUDIO = "audio",
}

// Exported so the container's mode-change handler can derive the current
// mode from the same single source of truth (used by its same-mode
// reselection no-op guard).
export function getEndOfPhraseMode(conversationControlSettings) {
  if (conversationControlSettings.audioEndOfPhraseDetectionEnabled) {
    return EndOfPhraseMode.AUDIO;
  }
  if (conversationControlSettings.endOfPhraseDetectionEnabled) {
    return EndOfPhraseMode.SEMANTIC;
  }
  return EndOfPhraseMode.OFF;
}

function EndOfPhraseModeRadio(props) {
  const { mode, onModeChange, locked } = props;

  // The whole card is a click target. The shared Radio primitive only
  // responds to clicks on the small radio circle, and putting onClick on
  // RadioField does not work reliably: Headless UI's prop merge wraps every
  // consumer `on*` handler with an `event.defaultPrevented` guard, and its
  // Label click handler calls preventDefault() -- so label clicks bubble to
  // the Field but Headless UI skips the Field-level onClick (its own
  // label->control click forwarding depends on ARIA `Element.role`
  // reflection, which older browsers lack). A PLAIN wrapping div is not
  // subject to that guard, so the card onClick lives there. The Radio's own
  // onClick is kept; a circle click fires both, but same-mode reselection
  // is a no-op in the container so duplicates are harmless.
  function cardProps(cardMode) {
    return {
      onClick: locked ? undefined : () => onModeChange(cardMode),
      className: clsx(
        "flex-1 border rounded-lg p-3",
        locked ? "cursor-default" : "cursor-pointer",
        mode === cardMode
          ? "border-zinc-900 bg-zinc-950/5 dark:border-white dark:bg-white/5"
          : "border-zinc-200 dark:border-zinc-800"
      ),
    };
  }

  return (
    <Field>
      <RadioGroup
        className="!space-y-0 flex w-full gap-4"
        value={mode}
        disabled={locked}
        // Keyboard support: arrow-key/space selection goes through Headless
        // UI's internal change(), which only reaches us via the group's
        // onChange (the onClick handlers only cover pointer interaction).
        // Duplicate same-mode calls are a no-op in the container, and
        // `disabled` already blocks this path when locked.
        onChange={onModeChange}
      >
        <div {...cardProps(EndOfPhraseMode.OFF)}>
          <RadioField>
            <Radio
              value={EndOfPhraseMode.OFF}
              onClick={() => onModeChange(EndOfPhraseMode.OFF)}
            />
            <Label>Off</Label>
            <Description>Reply as soon as transcription is generated</Description>
          </RadioField>
        </div>

        <div {...cardProps(EndOfPhraseMode.SEMANTIC)}>
          <RadioField>
            <Radio
              value={EndOfPhraseMode.SEMANTIC}
              onClick={() => onModeChange(EndOfPhraseMode.SEMANTIC)}
            />
            <Label>Semantic</Label>
            <Description>
              Reads the transcript to judge if the caller is done
            </Description>
          </RadioField>
        </div>

        <div {...cardProps(EndOfPhraseMode.AUDIO)}>
          <RadioField>
            <Radio
              value={EndOfPhraseMode.AUDIO}
              onClick={() => onModeChange(EndOfPhraseMode.AUDIO)}
            />
            <div
              data-slot="label"
              className="flex items-center gap-x-2"
            >
              <Label>Audio</Label>
              <Badge color="emerald">Recommended</Badge>
            </div>
            <Description>
              Listens to voice tone — faster.
            </Description>
          </RadioField>
        </div>
      </RadioGroup>
    </Field>
  );
}

function EndOfPhraseDetectionForm(props) {
  const {
    conversationControlSettings,
    onUpdate,
    onModeChange,
    errors,
    onUpdateError,
    locked,
  } = props;

  function handleUpdateTranscriptionReleaseTimeout(
    transcriptionReleaseTimeout
  ) {
    if (Number(transcriptionReleaseTimeout) < 1000) {
      onUpdateError({
        transcriptionReleaseTimeout: "Minimum duration is 1000 ms",
      });
    } else if (
      errors["transcriptionReleaseTimeout"] &&
      Number(transcriptionReleaseTimeout) >= 1000
    ) {
      onUpdateError({
        transcriptionReleaseTimeout: null,
      });
    }

    onUpdate({
      transcriptionReleaseTimeout,
    });
  }

  function handleUpdateAudioEndOfPhraseThreshold(audioEndOfPhraseThreshold) {
    onUpdate({ audioEndOfPhraseThreshold });
  }

  const mode = getEndOfPhraseMode(conversationControlSettings);
  const isOff = mode === EndOfPhraseMode.OFF;

  return (
    <div className="flex flex-col gap-y-4 px-6">
      <EndOfPhraseModeRadio
        mode={mode}
        onModeChange={onModeChange}
        locked={locked}
      />

      {!isOff && (
        <div className="flex gap-x-2 items-center text-sm text-gray-500">
          <InformationCircleIcon className="size-4 shrink-0" />
          <p>Enabling this turns off Speech Activity Detection interruption</p>
        </div>
      )}

      {/* Contextual fields render only for the selected mode -- unmounted
          (not merely disabled) for the other modes, and for Off entirely. */}
      {!isOff && (
        <div className="flex flex-col gap-y-4 max-w-xl">
          {mode === EndOfPhraseMode.AUDIO && (
            <InputComponent
              type="slider"
              min={0.3}
              max={0.9}
              step={0.05}
              disabled={locked}
              value={
                conversationControlSettings.audioEndOfPhraseThreshold ?? 0.7
              }
              label="Confidence Threshold"
              description="Higher values wait for clearer endings."
              onChange={(e) =>
                handleUpdateAudioEndOfPhraseThreshold(e.currentTarget.value)
              }
            />
          )}

          <InputComponent
            type="number"
            min={1000}
            error={errors["transcriptionReleaseTimeout"]}
            invalid={Boolean(errors["transcriptionReleaseTimeout"])}
            label="Maximum Wait (Milliseconds)"
            description="Reply anyway after waiting this long without a clear ending."
            disabled={locked}
            value={conversationControlSettings.transcriptionReleaseTimeout}
            onChange={(e) =>
              handleUpdateTranscriptionReleaseTimeout(e.target.value)
            }
          />
        </div>
      )}
    </div>
  );
}

export default function EndOfPhraseDetection(props) {
  const {
    onUpdateConversationControlSettings,
    onSave,
    onModeChange,
    settings,
    errors,
    onError,
    locked,
  } = props;

  const isSaveDisabled = Object.values(errors).some((value) => Boolean(value));

  return (
    <div className="flex flex-col gap-y-4 border rounded-lg min-w-[600px]">
      <div className="px-6 py-4 bg-gray-50 rounded-t-lg">
        <p className="text-base font-semibold text-gray-900">
          End Of Phrase Detection
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Hold the reply until the caller has finished speaking
        </p>
      </div>

      <EndOfPhraseDetectionForm
        conversationControlSettings={settings.conversationControlSettings}
        onUpdate={onUpdateConversationControlSettings}
        onModeChange={onModeChange}
        errors={errors}
        onUpdateError={onError}
        locked={locked}
      />

      <div className="bg-gray-50 rounded-b-lg px-6 py-3 text-right">
        {!locked && (
          <Button
            onClick={onSave}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        )}
      </div>
    </div>
  );
}
